import classNames from 'classnames';
import React, { ForwardedRef, ReactNode } from 'react';

import Body, { BodyProps } from './Body.js';
import Header, { HeaderProps } from './Header.js';

type CardSize = 'small' | 'medium' | 'large';

export interface CardProps {
  /**
   * The content to display in the card.
   */
  children?: ReactNode;

  /**
   * @ignore
   * Classes applied to the root element.
   */
  className?: string;

  /**
   * This prop controls the vertical padding of the container.
   *
   * small: 8px
   * medium: 12px
   * large: 20px
   *
   * @default medium
   */
  size?: CardSize;
}

interface CardComponent
  extends React.ForwardRefExoticComponent<
    CardProps & React.RefAttributes<HTMLDivElement>
  > {
  Body: React.ForwardRefExoticComponent<
    BodyProps & React.RefAttributes<HTMLDivElement>
  >;
  Header: React.ForwardRefExoticComponent<
    HeaderProps & React.RefAttributes<HTMLDivElement>
  >;
}

/**
 * Our card component that can be re-used for all instances of displaying content within
 * a group on a page. The layouts here have the same structure, but inset cards only use 12px
 * top & bottom padding, whereas stretched cards use 20px top & bottom padding. The content
 * gaps within are always set to 16px (when spacing with headers, the total gap should = 24px,
 * so a 4px margin should be applied on elements with a “hard edge”). Stretched cards should
 * be used with more dense content (over 3 sections), and inset cards should be used for more
 * simple use cases.
 *
 * If you have no header section, add relevant amount of padding to the top of the card to
 * compensate for the removal (keeps items vertically centered).
 *
 * @see https://www.figma.com/file/LdhxwbVqKRRLpyK5ekr4Q8/Component-Library?node-id=3691%3A7153&t=wk0v24Kh1MSLLzLy-1
 */
export const Card = React.forwardRef(
  (
    { children, className, size = 'medium' }: CardProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    return (
      <div
        className={classNames('ds-card', `ds-card--${size}`, className)}
        ref={ref}
      >
        {children}
      </div>
    );
  },
) as CardComponent;

Card.displayName = 'Card';
Card.Body = Body;
Card.Header = Header;

export default Card;
